import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import OwnerPageLayout from '../../components/layout/OwnerPageLayout';
import { getApiBaseUrl } from '../../apiBase';

const labelStyle = { display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' };
const fieldStyle = { marginBottom: '16px' };

function emptyForm() {
    return {
        legalName: '',
        companyNameEn: '',
        shortName: '',
        inn: '',
        ogrn: '',
        kpp: '',
        registrationNumber: '',
        legalAddress: '',
        phone: '',
        email: '',
        privacyEmail: '',
        website: '',
        privacyPolicyUrl: '/privacy',
        termsUrl: '/terms',
        offerUrl: '/offer',
        cookiePolicyUrl: '/cookies',
        refundPolicyUrl: '/refund',
        yooKassaShopId: '',
        yooKassaSecretKey: '',
        yooKassaConnected: false,
        stripeAccountId: '',
        stripePublishableKey: '',
        stripeSecretKey: '',
        stripeConnected: false,
        _yooMasked: null,
        _stripeMasked: null,
    };
}

function mapApi(data) {
    const base = emptyForm();
    if (!data) return base;
    return {
        ...base,
        legalName: data.companyNameRu ?? data.legalName ?? base.legalName,
        companyNameEn: data.companyNameEn ?? '',
        shortName: data.shortName ?? base.shortName,
        inn: data.taxNumber ?? data.inn ?? '',
        ogrn: data.ogrn ?? '',
        kpp: data.kpp ?? '',
        registrationNumber: data.registrationNumber ?? '',
        legalAddress: data.companyAddress ?? data.legalAddress ?? '',
        phone: data.supportPhone ?? data.phone ?? '',
        email: data.supportEmail ?? data.email ?? base.email,
        privacyEmail: data.privacyEmail ?? data.email ?? '',
        website: data.website ?? base.website,
        privacyPolicyUrl: data.privacyPolicyUrl ?? base.privacyPolicyUrl,
        termsUrl: data.termsUrl ?? base.termsUrl,
        offerUrl: data.offerUrl ?? base.offerUrl,
        cookiePolicyUrl: data.cookiePolicyUrl ?? base.cookiePolicyUrl,
        refundPolicyUrl: data.refundPolicyUrl ?? base.refundPolicyUrl,
        yooKassaShopId: data.yooKassaShopId ?? '',
        yooKassaSecretKey: '',
        yooKassaConnected: !!data.yooKassaConnected,
        stripeAccountId: data.stripeAccountId ?? '',
        stripePublishableKey: data.stripePublishableKey ?? '',
        stripeSecretKey: '',
        stripeConnected: !!data.stripeConnected,
        _yooMasked: data.yooKassaSecretMasked ?? null,
        _stripeMasked: data.stripeSecretMasked ?? null,
    };
}

function adminApi() {
    return axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
}

const LEGAL_REQUISITE_FIELDS = [
    'legalName',
    'companyNameEn',
    'inn',
    'ogrn',
    'kpp',
    'registrationNumber',
    'legalAddress',
    'phone',
    'email',
    'privacyEmail',
];

const URL_FIELDS = [
    'website',
    'privacyPolicyUrl',
    'termsUrl',
    'offerUrl',
    'cookiePolicyUrl',
    'refundPolicyUrl',
    'shortName',
];

export default function PlatformSettingsPage() {
    const { t } = useTranslation(['platformSettings', 'orgSettings', 'common', 'nav']);
    const [tab, setTab] = useState('ruBy');
    const [form, setForm] = useState(emptyForm);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState('');
    const [msgIsError, setMsgIsError] = useState(false);
    const [testingYoo, setTestingYoo] = useState(false);
    const [testingStripe, setTestingStripe] = useState(false);

    const basePath = '/platform/organization-settings';

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await adminApi().get(basePath);
            setForm(mapApi(res.data));
        } catch {
            setMsg(t('orgSettings:loadError'));
            setMsgIsError(true);
        }
        setLoading(false);
    }, [t]);

    useEffect(() => { load(); }, [load]);

    function setField(key, value) {
        setForm(prev => ({ ...prev, [key]: value }));
    }

    async function save(extra = {}) {
        setSaving(true);
        setMsg('');
        setMsgIsError(false);
        const payload = { ...form, ...extra };
        delete payload._yooMasked;
        delete payload._stripeMasked;
        if (!payload.yooKassaSecretKey?.trim()) delete payload.yooKassaSecretKey;
        if (!payload.stripeSecretKey?.trim()) delete payload.stripeSecretKey;
        delete payload.yooKassaConnected;
        delete payload.stripeConnected;
        try {
            const res = await adminApi().put(basePath, payload);
            setForm(mapApi(res.data));
            setMsg(t('orgSettings:saved'));
            setMsgIsError(false);
        } catch (err) {
            setMsg(err.response?.data?.message || t('orgSettings:loadError'));
            setMsgIsError(true);
        }
        setSaving(false);
    }

    async function testYoo() {
        setTestingYoo(true);
        setMsg('');
        setMsgIsError(false);
        try {
            const res = await adminApi().post(`${basePath}/test-yookassa`, {
                shopId: form.yooKassaShopId || undefined,
                secretKey: form.yooKassaSecretKey || undefined,
            });
            if (res.data?.success) {
                setMsg(t('orgSettings:payments.testOk', { shopName: res.data.shopName || 'OK' }));
                setForm(f => ({ ...f, yooKassaConnected: true }));
            } else {
                setMsg(t('orgSettings:payments.testFail', { error: res.data?.error || '?' }));
                setMsgIsError(true);
            }
        } catch (err) {
            setMsg(t('orgSettings:payments.testFail', { error: err.message }));
            setMsgIsError(true);
        }
        setTestingYoo(false);
    }

    async function testStripe() {
        setTestingStripe(true);
        setMsg('');
        setMsgIsError(false);
        try {
            const res = await adminApi().post(`${basePath}/test-stripe`);
            setMsg(res.data?.success
                ? t('orgSettings:payments.testOk', { shopName: t('orgSettings:payments.stripe', { defaultValue: 'International cards' }) })
                : t('orgSettings:payments.testFail', { error: res.data?.error || '?' }));
            setMsgIsError(!res.data?.success);
        } catch (err) {
            setMsg(t('orgSettings:payments.testFail', { error: err.message }));
            setMsgIsError(true);
        }
        setTestingStripe(false);
    }

    const tabs = useMemo(() => [
        { key: 'ruBy', label: t('platformSettings:tab.ruBy') },
        { key: 'cis', label: t('platformSettings:tab.cis') },
        { key: 'intl', label: t('platformSettings:tab.intl') },
        { key: 'requisites', label: t('platformSettings:tab.requisites') },
    ], [t]);

    const yooBlock = (
        <div style={{ marginBottom: 24, padding: 16, background: '#f8fafc', borderRadius: 12 }}>
            <h4 style={{ margin: '0 0 12px' }}>{t('orgSettings:payments.yookassa')}</h4>
            <div style={fieldStyle}>
                <label style={labelStyle}>{t('orgSettings:payments.shopId')}</label>
                <input className="sp-input" value={form.yooKassaShopId} onChange={e => setField('yooKassaShopId', e.target.value)} />
            </div>
            <div style={fieldStyle}>
                <label style={labelStyle}>{t('orgSettings:payments.secretKey')}</label>
                <input
                    className="sp-input"
                    type="password"
                    value={form.yooKassaSecretKey}
                    placeholder={form._yooMasked || t('orgSettings:payments.changeKey')}
                    onChange={e => setField('yooKassaSecretKey', e.target.value)}
                    autoComplete="off"
                />
            </div>
            <button type="button" className="sp-btn sp-btn--primary" disabled={testingYoo} onClick={testYoo}>
                {testingYoo ? t('common:loading') : t('orgSettings:payments.testConnection')}
            </button>
        </div>
    );

    const stripeBlock = (
        <div style={{ marginBottom: 24, padding: 16, background: '#f8fafc', borderRadius: 12 }}>
            <h4 style={{ margin: '0 0 12px' }}>{t('orgSettings:payments.stripe')}</h4>
            <div style={fieldStyle}>
                <label style={labelStyle}>{t('orgSettings:payments.accountId')}</label>
                <input className="sp-input" value={form.stripeAccountId} onChange={e => setField('stripeAccountId', e.target.value)} />
            </div>
            <div style={fieldStyle}>
                <label style={labelStyle}>{t('orgSettings:payments.publishableKey')}</label>
                <input className="sp-input" value={form.stripePublishableKey} onChange={e => setField('stripePublishableKey', e.target.value)} />
            </div>
            <div style={fieldStyle}>
                <label style={labelStyle}>{t('orgSettings:payments.secretKey')}</label>
                <input
                    className="sp-input"
                    type="password"
                    value={form.stripeSecretKey}
                    placeholder={form._stripeMasked || t('orgSettings:payments.changeKey')}
                    onChange={e => setField('stripeSecretKey', e.target.value)}
                    autoComplete="off"
                />
            </div>
            <button type="button" className="sp-btn sp-btn--primary" disabled={testingStripe} onClick={testStripe}>
                {testingStripe ? t('common:loading') : t('orgSettings:payments.testConnection')}
            </button>
        </div>
    );

    if (loading) {
        return (
            <OwnerPageLayout title={t('platformSettings:pageTitle')} maxWidth={800}>
                <div className="sp-loading-page">{t('common:loading')}</div>
            </OwnerPageLayout>
        );
    }

    return (
        <OwnerPageLayout title={t('platformSettings:pageTitle')} maxWidth={800}>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>{t('platformSettings:subtitle')}</p>
            {msg && (
                <p style={{
                    marginBottom: 12,
                    fontSize: 13,
                    padding: '10px 14px',
                    borderRadius: 8,
                    background: msgIsError ? '#fdecea' : '#e8f5e9',
                    color: msgIsError ? '#c0392b' : '#2e7d32',
                }}>
                    {msg}
                </p>
            )}

            <div className="sp-tabs" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
                {tabs.map(item => (
                    <button
                        key={item.key}
                        type="button"
                        className={`sp-tab${tab === item.key ? ' sp-tab--active' : ''}`}
                        onClick={() => setTab(item.key)}
                    >
                        {item.label}
                    </button>
                ))}
            </div>

            <div className="sp-panel">
                {tab === 'ruBy' && (
                    <>
                        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>{t('platformSettings:ruByHint')}</p>
                        {yooBlock}
                        <button type="button" className="sp-btn sp-btn--ghost" disabled={saving} onClick={() => save()}>
                            {saving ? t('common:loading') : t('orgSettings:save')}
                        </button>
                    </>
                )}
                {tab === 'cis' && (
                    <>
                        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>{t('platformSettings:cisHint')}</p>
                        {yooBlock}
                        {stripeBlock}
                        <button type="button" className="sp-btn sp-btn--ghost" disabled={saving} onClick={() => save()}>
                            {saving ? t('common:loading') : t('orgSettings:save')}
                        </button>
                    </>
                )}
                {tab === 'intl' && (
                    <>
                        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>{t('platformSettings:intlHint')}</p>
                        {stripeBlock}
                        <button type="button" className="sp-btn sp-btn--ghost" disabled={saving} onClick={() => save()}>
                            {saving ? t('common:loading') : t('orgSettings:save')}
                        </button>
                    </>
                )}
                {tab === 'requisites' && (
                    <>
                        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
                            {t('platformSettings:requisitesHint')}
                        </p>
                        <h4 style={{ margin: '0 0 12px' }}>{t('platformSettings:legalSectionTitle')}</h4>
                        {LEGAL_REQUISITE_FIELDS.map(key => (
                            <div style={fieldStyle} key={key}>
                                <label style={labelStyle}>{t(`platformSettings:fields.${key}`, { defaultValue: key })}</label>
                                <input className="sp-input" value={form[key] ?? ''} onChange={e => setField(key, e.target.value)} />
                            </div>
                        ))}
                        <h4 style={{ margin: '24px 0 12px' }}>{t('platformSettings:urlsSectionTitle')}</h4>
                        {URL_FIELDS.map(key => (
                            <div style={fieldStyle} key={key}>
                                <label style={labelStyle}>{t(`platformSettings:fields.${key}`, { defaultValue: key })}</label>
                                <input className="sp-input" value={form[key] ?? ''} onChange={e => setField(key, e.target.value)} />
                            </div>
                        ))}
                        <button type="button" className="sp-btn sp-btn--primary" disabled={saving} onClick={() => save()}>
                            {saving ? t('common:loading') : t('orgSettings:save')}
                        </button>
                    </>
                )}
            </div>
        </OwnerPageLayout>
    );
}
